import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import { Button, FlexContainer } from '@act/storybook';
import { MenuItem, Select, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Link, useNavigate } from 'react-router-dom';
import { scheduleReportPrintSectionFit } from '../utils/reportPrintFit.util';

export interface ReportPrintToolbarSection {
  sectionId: string;
  title: string;
}

export interface ReportPrintToolbarProps {
  title: string;
  subtitle: string;
  reportId: string;
  sections: ReportPrintToolbarSection[];
  activeSectionId: string;
}

/**
 * Print-route controls: report context, section picker, and print trigger.
 * @param props - Report context and included sections for section-scoped preview.
 */
export const ReportPrintToolbar = ({
  title,
  subtitle,
  reportId,
  sections,
  activeSectionId,
}: ReportPrintToolbarProps) => {
  const navigate = useNavigate();

  const handleSectionChange = (event: SelectChangeEvent) => {
    const next = event.target.value;
    if (next === '__all__') {
      navigate(`/${reportId}/print`);
      return;
    }
    navigate(`/${reportId}/print?section=${encodeURIComponent(next)}`);
  };

  const handlePrint = () => {
    scheduleReportPrintSectionFit();
    window.requestAnimationFrame(() => {
      window.print();
    });
  };

  return (
    <div className="reports-print-toolbar reports-print-route-header">
      <FlexContainer
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap={true}
        gap={2}
        width="100%"
        height="auto"
        overflowAllVisible={true}
      >
        <div className="reports-print-toolbar__context">
          <FlexContainer
            direction="column"
            gap={0.25}
            width="100%"
            height="auto"
            overflowAllVisible={true}
          >
          <Typography variant="h6" component="p" noWrap={true}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" component="p">
              {subtitle}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" component="p">
            In the print dialog, disable Headers and footers to hide the URL.
          </Typography>
          <Link to={`/${reportId}/view`}>
            <Typography variant="body2" color="text.secondary" component="span">
              ← Back to report
            </Typography>
          </Link>
          </FlexContainer>
        </div>

        <div className="reports-print-toolbar__actions">
          <FlexContainer
            direction="row"
            alignItems="center"
            gap={1}
            height="auto"
            overflowAllVisible={true}
          >
          {sections.length > 0 ? (
            <Select
              id="report-print-section-select"
              className="reports-print-toolbar__section-select"
              size="small"
              value={activeSectionId || '__all__'}
              onChange={handleSectionChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Report section' }}
            >
              <MenuItem {...generateDatadogTrackingProps('report-print-toolbar-menu-item-1')} value="__all__">All sections</MenuItem>
              {sections.map(section => (
                <MenuItem {...generateDatadogTrackingProps('report-print-toolbar-menu-item-2')} key={section.sectionId} value={section.sectionId}>
                  {section.title}
                </MenuItem>
              ))}
            </Select>
          ) : null}
          <Button
            variant="contained"
            trackingName="reports-print-trigger"
            onClick={handlePrint}
          >
            Print
          </Button>
          </FlexContainer>
        </div>
      </FlexContainer>
    </div>
  );
};
